import React, { useEffect, useState } from 'react';
import { Image, FlatList, ActivityIndicator } from "react-native"
import type { PollCampaignLeaderProps, PollCampaignProps, PublicPlayerProps } from '../../types';
import { PollCampaignApi } from '../api';
import { Icons } from '../../Components';
import { useColors } from '../../constants/useColors';
import { Button, Text, View } from '../../Components/Themed';
import Pagination from '../../Components/Pagination';

type CampaignLeaderboardProps = {
    poll_campaign:PollCampaignProps,
    view_mode: "short"|"full",
    height: number,
    mode:'desktop'|'mobile',
    onShowFullLeaderboard?: () => void,
    onClose?:() => void
}

const CampaignLeaderboard = ({ poll_campaign, height, mode, view_mode, onShowFullLeaderboard, onClose }:CampaignLeaderboardProps) => {
    const Colors = useColors();
    const [ leaderboard_data, setLeaderData ] = useState<{
        loading:boolean,
        offset:number,
        leaders:PollCampaignLeaderProps[],
        players:PublicPlayerProps[],
        my_leader?: PollCampaignLeaderProps
    }>({
        loading:false,
        offset:0,
        leaders:[],
        players:[]
    })
    const { loading, leaders, players, my_leader, offset } = leaderboard_data;
    const me = players.find(p => p.player_id == my_leader?.player_id);

    let filtered_leaders = [ ...leaders ]
    if(view_mode == 'short'){
        filtered_leaders = filtered_leaders.sort((a,b) => a.place - b.place).slice(0,3);
    }

    useEffect(() => {
        getDataFromServer(0);
    },[poll_campaign.poll_campaign_id])

    const getDataFromServer = async(offset:number) => {
        setLeaderData({ ...leaderboard_data, loading:true })
        const ls = await PollCampaignApi.getLeadersByCampaignId(poll_campaign.poll_campaign_id, offset)
        const player_ids = ls.poll_campaign_leaders.map(l => l.player_id);
        if(ls.my_leaderboard){
            let my_id = player_ids.find(id => id == ls.my_leaderboard?.player_id);
            if(!my_id){ player_ids.push(ls.my_leaderboard.player_id) }
        }
        const ps = await PollCampaignApi.getPlayersByPlayerIds(player_ids);
        setLeaderData({
            ...leaderboard_data,
            players:ps,
            offset,
            loading: false,
            leaders: ls.poll_campaign_leaders,
            my_leader: ls.my_leaderboard
        })
    }

    const renderTrophy = (place:number) => {
        switch(place){
            case 1: return <Icons.TrophyIcon color={Colors.text.gold} size={16}/>
            case 2: return <Icons.TrophyIcon color={Colors.text.silver} size={16}/>
            case 3: return <Icons.TrophyIcon color={Colors.text.bronze} size={16}/>
            default: return <></>
        }
    }


    const renderLeaders = (data: {item:PollCampaignLeaderProps, index:number}) => {
        const player = players.find(p => p.player_id == data.item.player_id);
        if(!player){ return <></> }
        return (
            <View float style={{ flexDirection:'row', alignItems:'center', margin:4, padding:5, borderRadius:22 }}>
                <View transparent nativeID="place" style={{ margin:5, height:20, width:20, justifyContent:'center', alignItems:'center', borderRadius:100, borderWidth:1 }}>
                    <Text size={10} color={Colors.text.h1} weight='bold' textAlign="center">{offset > 0 ? offset:''}{data.index+1}</Text>
                </View>
                <View transparent nativeID="player" style={{ flex:1, flexDirection:'row', alignItems:'center' }}>
                    <View nativeID="profile_pic" style={{ marginRight:10 }}>
                        <Image
                            source={{ uri: player.profile_pic && player.profile_pic != '' ? player.profile_pic : 'https://res.cloudinary.com/hoabts6mc/image/upload/v1689262384/default-avatar_bbkn2t.png' }}
                            style={{ height:35, width:35, borderRadius:100 }}
                            resizeMode='cover'
                        />
                    </View>
                    <View transparent style={{ flex:1 }} nativeID="name">
                        <Text size={14} color={Colors.text.h1} weight='bold'>{player.username}</Text>
                        <Text style={{ marginTop:3 }} size={12} color={Colors.text.h1} weight='regular'>{data.item.winnings.toFixed(2)} Points</Text>
                    </View>
                    <View float style={{ padding:5, borderRadius:100 }}>
                        {renderTrophy(data.index+1)}
                    </View>
                </View>
            </View>
        )
    }

    return (
        <View float style={{ flexGrow:1, maxHeight: height-20, width: mode == 'desktop'?500:undefined  }}>
            {my_leader && me ?
            <View type='header' nativeID="my_results" style={{ padding:10 }}>
                <Text style={{ padding:10 }} size={14} theme='h1' weight='bold'>My Results</Text>
                <View float style={{ flexDirection:'row', alignItems:'center', margin:4, padding:5, borderRadius:22 }}>
                    <View transparent nativeID="player" style={{ flex:1, flexDirection:'row', alignItems:'center' }}>
                        <View transparent nativeID="profile_pic" style={{ marginRight:10 }}>
                            <Image
                                source={{ uri: me.profile_pic && me.profile_pic != '' ? me.profile_pic : 'https://res.cloudinary.com/hoabts6mc/image/upload/v1689262384/default-avatar_bbkn2t.png' }}
                                style={{ height:35, width:35, borderRadius:100 }}
                                resizeMode='cover'
                            />
                        </View>
                        <View transparent style={{ flex:1 }} nativeID="name">
                            <Text size={14} theme='h1' weight='bold'>{me.username}</Text>
                            <Text style={{ marginTop:3 }} size={12} theme='description' weight='regular'>{my_leader.winnings.toFixed(2)} Points</Text>
                        </View>
                        <View float style={{ padding:5, borderRadius:100 }}>
                            {renderTrophy(my_leader.place)}
                        </View>
                    </View>
                </View>
            </View>
            :<></>}
            <View style={{flexGrow:1, padding:15, borderTopRightRadius:view_mode=='full'?22:0, borderTopLeftRadius:view_mode=='full'?22:0 }}>
                {loading ?
                <ActivityIndicator size='large' color={Colors.text.h1} style={{ padding:20, alignSelf:'center' }} />
                :<></>}
                <FlatList
                    data={filtered_leaders}
                    renderItem={renderLeaders}
                    keyExtractor={((item) => item.player_id.toString())}
                />
            </View>
            {view_mode == 'full' ?
            <View>
                <Pagination
                    offset={offset}
                    onNext={() => getDataFromServer(offset + 1)}
                    onPrevious={() => getDataFromServer(offset - 1)}
                />
            </View>
            :<></>}
            {view_mode == 'short' && onShowFullLeaderboard ?
            <View type='footer' style={{ flexDirection:'row', padding:10 }}>
                <Button
                    title='See Full Leaderboard'
                    style={{ flex:1, alignSelf:'center', padding:0 }}
                    type='text'
                    onPress={() => onShowFullLeaderboard()}
                />
            </View>
            :<></>}
            {onClose ?
            <View type='footer' style={{ flexDirection:'row', padding:10 }}>
                <Button
                    title='CLOSE'
                    style={{ flex:1 }}
                    type='close'
                    onPress={() => onClose()}
                />
            </View>
            :<></>}
        </View>
    )
}

export default CampaignLeaderboard